import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Calculator, AlertTriangle, CheckCircle, XCircle, 
  Plus, Trash2, ChevronDown, ChevronUp, Scale, FileText
} from 'lucide-react';
import { 
  calculateFullScore, determineCard, getPenaltyTypes, 
  getSurfaceProtocolInfo, formatPoints, getMeasurementUnit 
} from '../utils/penaltyEngine';

const PenaltyCalculator = ({ 
  t, 
  language, 
  federation = 'AIDA', 
  eventCode = 'DYN',
  announced = 0,
  realized = 0,
  onSave,
  passageId,
  performanceDuration = 0,
  existingPenalties = []
}) => {
  const [penalties, setPenalties] = useState(existingPenalties);
  const isSTAEvent = eventCode === 'STA';

  const formatSTATime = (seconds) => {
    if (!seconds && seconds !== 0) return '00:00';
    const totalSec = Math.round(Number(seconds));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const parseSTATime = (val) => {
    if (!val) return 0;
    if (typeof val === 'number') return val;
    const parts = String(val).split(':');
    if (parts.length === 2) {
      return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
    }
    return parseFloat(val) || 0;
  };

  // For STA, rpValue is stored as mm:ss string; for others, as number
  const [rpValue, setRpValue] = useState(() => {
    if (isSTAEvent && realized > 0) return formatSTATime(realized);
    return realized || '';
  });

  // Update RP when pre-filled value changes (e.g., from Touch 25m or stopwatch)
  useEffect(() => {
    if (realized > 0) {
      setRpValue(isSTAEvent ? formatSTATime(realized) : realized);
    }
  }, [realized, isSTAEvent]);
  const [showSP, setShowSP] = useState(false);

  const penaltyTypes = useMemo(() => getPenaltyTypes(federation, eventCode), [federation, eventCode]);
  const spInfo = useMemo(() => getSurfaceProtocolInfo(federation), [federation]);
  const unit = getMeasurementUnit(eventCode, federation);

  const result = useMemo(() => {
    const rpSeconds = isSTAEvent ? parseSTATime(rpValue) : (parseFloat(rpValue) || 0);
    return calculateFullScore({
      announced: announced,
      realized: rpSeconds,
      eventCode,
      federation,
      penalties
    });
  }, [announced, rpValue, eventCode, federation, penalties, isSTAEvent]);

  const cardColor = determineCard(result);

  const addPenalty = (type) => {
    if (type.isDQ) {
      setPenalties(prev => [...prev, { code: type.code, label: type.label, value: 1 }]);
      return;
    }
    setPenalties(prev => [...prev, { 
      code: type.code, 
      label: type.label, 
      value: type.hasInput ? 0 : 1,
      inputType: type.inputType,
      points: type.points 
    }]);
  };

  const updatePenaltyValue = (index, value) => {
    setPenalties(prev => prev.map((p, i) => i === index ? { ...p, value: parseFloat(value) || 0 } : p));
  };

  const removePenalty = (index) => {
    setPenalties(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (onSave) {
      const penaltyDetails = {};
      for (const p of penalties) {
        if (p.code === 'LATE') penaltyDetails.LATE = { seconds: p.value };
        else if (p.code === 'EARLY') penaltyDetails.EARLY = { seconds: p.value };
        else if (p.code === 'START_TURN') penaltyDetails.TURN = { count: p.value };
        else if (p.code === 'PULL_POOL') penaltyDetails.GRAB = { count: p.value };
        else if (p.code === 'GRAB') penaltyDetails.GRAB = { count: p.value };
        else if (p.code === 'DOLPHIN_KICK') penaltyDetails.DOLPHIN_KICK = { count: p.value };
        else if (p.code?.startsWith('DQ_')) penaltyDetails[p.code.replace('DQ_', '')] = { active: true };
      }
      const rpSeconds = isSTAEvent ? parseSTATime(rpValue) : (parseFloat(rpValue) || 0);
      onSave({
        passageId,
        distance_covered: rpSeconds,
        performance_time_seconds: isSTAEvent ? rpSeconds : (performanceDuration || null),
        card_result: cardColor,
        penalty_details: penaltyDetails,
        final_score: result.finalScore,
        is_dq: result.isDQ
      });
    }
  };

  const grouped = {
    timing: penaltyTypes.filter(p => p.group === 'timing'),
    pool: penaltyTypes.filter(p => p.group === 'pool'),
    depth: penaltyTypes.filter(p => p.group === 'depth'),
    dq: penaltyTypes.filter(p => p.group === 'dq'),
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/80 to-blue-900/60 border-blue-700/40" data-testid="penalty-calculator">
      <CardHeader className="pb-2">
        <CardTitle className="text-blue-200 flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Calculator size={16} />
            {t('penaltyCalculator') || 'Penalty Calculator'}
            <Badge className={`${federation === 'AIDA' ? 'bg-violet-700' : 'bg-teal-700'} text-xs`}>
              {federation}
            </Badge>
          </div>
          <Badge className="bg-blue-800/60 text-blue-200 text-xs">{eventCode}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* AP / RP Input */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-blue-400 text-xs mb-1 block">{t('announcedPerformance') || 'AP'}</label>
            <div className="bg-blue-800/30 rounded px-3 py-2 text-white font-mono text-sm">
              {isSTAEvent ? formatSTATime(announced) : `${announced} ${unit === 'time' ? 's' : 'm'}`}
            </div>
          </div>
          <div>
            <label className="text-blue-400 text-xs mb-1 block">{t('realizedPerformance') || 'RP'}</label>
            {isSTAEvent ? (
              <Input
                type="text"
                value={rpValue}
                onChange={(e) => {
                  let val = e.target.value.replace(/[^0-9:]/g, '');
                  if (val.length === 2 && !val.includes(':') && rpValue.length < val.length) val = val + ':';
                  if (val.length > 5) val = val.slice(0, 5);
                  setRpValue(val);
                }}
                className="bg-blue-800/30 border-blue-600/50 text-white text-sm h-9"
                placeholder="mm:ss"
                data-testid="rp-input"
              />
            ) : (
              <Input
                type="number"
                step="0.1"
                value={rpValue}
                onChange={(e) => setRpValue(e.target.value)}
                className="bg-blue-800/30 border-blue-600/50 text-white text-sm h-9"
                placeholder={unit === 'time' ? 'seconds' : 'meters'}
                data-testid="rp-input"
              />
            )}
          </div>
        </div>

        {/* Score Breakdown */}
        <div className={`rounded-lg p-3 border ${
          result.isDQ ? 'bg-red-900/40 border-red-600/50' : 
          result.totalPenaltyPoints > 0 ? 'bg-yellow-900/30 border-yellow-600/50' : 
          'bg-green-900/30 border-green-600/50'
        }`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-white font-bold text-lg" data-testid="final-score">
              {result.isDQ ? 'DQ (0 pts)' : formatPoints(result.finalScore, federation)}
            </span>
            <div className={`w-8 h-5 rounded border-2 ${
              cardColor === 'white' ? 'bg-white border-gray-300' :
              cardColor === 'yellow' ? 'bg-yellow-400 border-yellow-500' :
              'bg-red-600 border-red-700'
            }`} data-testid="card-indicator" />
          </div>
          {result.breakdown.map((item, i) => (
            <div key={i} className={`flex justify-between text-xs py-0.5 ${
              item.type === 'base' ? 'text-green-300' : 
              item.type === 'dq' ? 'text-red-400' : 'text-yellow-300'
            }`}>
              <span>{item.label}</span>
              <span className="font-mono">
                {item.type === 'base' ? `+${item.points}` : item.type === 'dq' ? 'DQ' : item.points}
              </span>
            </div>
          ))}
        </div>

        {/* Add Penalties */}
        <div className="space-y-2">
          <p className="text-blue-300 text-xs font-semibold uppercase tracking-wider">{t('addPenalties') || 'Add Penalties'}</p>
          
          {/* Timing Penalties */}
          <div className="flex flex-wrap gap-1.5">
            {grouped.timing.filter(p => !p.auto).map(type => (
              <Button
                key={type.code}
                onClick={() => addPenalty(type)}
                size="sm"
                variant="outline"
                className="border-orange-600/50 text-orange-300 hover:bg-orange-800/30 text-xs h-7 px-2"
              >
                <Plus size={10} className="mr-1" />{type.label}
              </Button>
            ))}
          </div>

          {/* Discipline-specific */}
          {(grouped.pool.length > 0 || grouped.depth.length > 0) && (
            <div className="flex flex-wrap gap-1.5">
              {[...grouped.pool, ...grouped.depth].map(type => (
                <Button
                  key={type.code}
                  onClick={() => addPenalty(type)}
                  size="sm"
                  variant="outline"
                  className="border-yellow-600/50 text-yellow-300 hover:bg-yellow-800/30 text-xs h-7 px-2"
                >
                  <Plus size={10} className="mr-1" />{type.label}
                </Button>
              ))}
            </div>
          )}

          {/* DQ Reasons */}
          <div className="flex flex-wrap gap-1.5">
            {grouped.dq.map(type => (
              <Button
                key={type.code}
                onClick={() => addPenalty(type)}
                size="sm"
                variant="outline"
                className="border-red-600/50 text-red-300 hover:bg-red-800/30 text-xs h-7 px-2"
              >
                <XCircle size={10} className="mr-1" />{type.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Active Penalties */}
        {penalties.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-yellow-300 text-xs font-semibold">{t('activePenalties') || 'Active Penalties'}</p>
            {penalties.map((p, i) => (
              <div key={i} className={`flex items-center justify-between rounded px-2 py-1.5 ${
                p.code?.startsWith('DQ_') ? 'bg-red-900/40' : 'bg-yellow-900/30'
              }`}>
                <span className={`text-xs ${p.code?.startsWith('DQ_') ? 'text-red-300' : 'text-yellow-200'}`}>
                  {p.label}
                </span>
                <div className="flex items-center gap-2">
                  {p.inputType && (
                    <Input
                      type="number"
                      value={p.value}
                      onChange={(e) => updatePenaltyValue(i, e.target.value)}
                      className="bg-blue-800/30 border-blue-600/50 text-white text-xs h-6 w-16"
                      min="0"
                    />
                  )}
                  <Button
                    onClick={() => removePenalty(i)}
                    size="sm"
                    variant="ghost"
                    className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                  >
                    <Trash2 size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Surface Protocol Reference */}
        <div>
          <Button
            onClick={() => setShowSP(!showSP)}
            variant="ghost"
            size="sm"
            className="text-blue-400 text-xs hover:text-blue-300 p-0 h-auto"
          >
            <Scale size={12} className="mr-1" />
            {t('surfaceProtocol') || 'Surface Protocol'} ({federation}: {spInfo.timeLimit}s)
            {showSP ? <ChevronUp size={12} className="ml-1" /> : <ChevronDown size={12} className="ml-1" />}
          </Button>
          {showSP && (
            <div className="mt-1.5 bg-blue-900/30 rounded p-2 text-xs text-blue-300 space-y-1">
              <p className="font-semibold text-blue-200">{t('timeLimit') || 'Time Limit'}: {spInfo.timeLimit}s</p>
              <ul className="list-disc list-inside space-y-0.5">
                {spInfo.tasks.map((task, i) => <li key={i}>{task}</li>)}
              </ul>
            </div>
          )}
        </div>

        {/* Save Button */}
        {onSave && (
          <Button
            onClick={handleSave}
            className="w-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white"
            size="sm"
            data-testid="save-performance-btn"
          >
            <CheckCircle size={14} className="mr-1.5" />
            {t('savePerformance') || 'Save Performance'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default PenaltyCalculator;
